import React, { createContext, useCallback, useEffect, useMemo } from 'react';
import {
  CreateLocalTrackOptions,
  ConnectOptions,
  LocalAudioTrack,
  LocalVideoTrack,
  Room,
  TwilioError,
} from 'twilio-video';

import SelectedParticipantProvider from 'screens/TelehealthScreen/components/SelectedParticipantProvider';

import { ErrorCallback } from 'screens/TelehealthScreen/utils/twilioCustomTypes';
import { LocalParticipantEvents, RoomEvents } from 'screens/TelehealthScreen/utils/enums';
import { useFullScreenToggle } from 'screens/TelehealthScreen/hooks';
import { saveTelehealthLog } from 'screens/TelehealthScreen/utils/helpers';
import useLiveRef from 'hooks/useLiveRef';

import { useLocalTracks, useRoom } from './hooks';

/*
 *  The hooks used by the VideoProvider component are different than the hooks found in the 'hooks/' directory.
 *  The hooks in the 'hooks/' directory can be used anywhere in a video application, and they can be used any
 *  number of times.
 *  the hooks in the 'VideoProvider/' directory are intended to be used by the VideoProvider component only.
 *  Using these hooks elsewhere in the application may cause problems as these hooks should not be used more
 *  than once in an application.
 *
 *  IMPORTANT: The point here is: That "hooks" mentioned above are just peace of code, business logic
 *  that belongs to this page encapsulated in hooks, maybe to make this file smaller, and this is why
 *  we should not use them in other places.
 */

const CONNECT_OPTIONS: ConnectOptions = {
  bandwidthProfile: {
    video: {
      mode: 'collaboration',
      dominantSpeakerPriority: 'high',
      trackSwitchOffMode: 'detected',
    },
  },
  dominantSpeaker: true,
  networkQuality: { local: 1, remote: 1 },
  preferredVideoCodecs: [{ codec: 'VP8', simulcast: false }],
  video: { height: 480, frameRate: 24, width: 640 },
};

export interface VideoContextProps {
  connect: (token: string) => Promise<void>;
  getAudioAndVideoTracks: () => Promise<void>;
  getLocalAudioTrack: (deviceId?: string) => Promise<LocalAudioTrack>;
  getLocalVideoTrack: (newOptions?: CreateLocalTrackOptions) => Promise<LocalVideoTrack>;
  isAcquiringLocalTracks: boolean;
  isConnecting: boolean;
  localTracks: (LocalAudioTrack | LocalVideoTrack)[];
  onError: ErrorCallback;
  removeLocalVideoTrack: () => void;
  room: Room | null;
}

export const VideoContext = createContext<VideoContextProps>(null!);

interface VideoProviderProps {}

const VideoProvider: React.FC<VideoProviderProps> = ({ children }) => {
  const onErrorCallback = useCallback((error: TwilioError | Error, source?: string) => {
    const normalizedError = {
      host: window.location.host,
      errorStack: error,
    };
    saveTelehealthLog(`Patient/Guest - Telehealth generic error${source ? ` - ${source}` : ''}`, normalizedError);
  }, []);

  const {
    getAudioAndVideoTracks,
    getLocalAudioTrack,
    getLocalVideoTrack,
    isAcquiringLocalTracks,
    localTracks,
    removeLocalAudioTrack,
    removeLocalVideoTrack,
  } = useLocalTracks();
  const { room, isConnecting, connect } = useRoom(localTracks, onErrorCallback, CONNECT_OPTIONS);
  const [isFullScreen, toggleFullScreen] = useFullScreenToggle();

  const onDisconnected = useCallback(
    (_?: Room, error?: TwilioError) => {
      if (error) {
        onErrorCallback(error, 'Video Patient/Guest - Error on disconnect');
      }

      // clear local tracks
      removeLocalAudioTrack();
      removeLocalVideoTrack();

      // exit fullscreen mode
      if (isFullScreen) {
        toggleFullScreen();
      }

      saveTelehealthLog('Patient/Guest - Video Provider - destroying media tracks');
    },
    [isFullScreen, onErrorCallback, removeLocalAudioTrack, removeLocalVideoTrack, toggleFullScreen],
  );

  // live ref of disconnect function to be called on component unmount
  const disconnectRef = useLiveRef(onDisconnected);

  useEffect(() => {
    saveTelehealthLog('Adding handler to kill medias in case of unexpected disconnect');
    // this code should run only on component unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => disconnectRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Register callback functions to be called on room disconnect.
  useEffect(() => {
    if (room) {
      room.on(RoomEvents.Disconnected, onDisconnected);

      return () => {
        room.off(RoomEvents.Disconnected, onDisconnected);
      };
    }
  }, [room, onDisconnected]);

  // logging publication errors on local user media tracks
  useEffect(() => {
    if (room && room.localParticipant) {
      room.localParticipant.on(LocalParticipantEvents.TrackPublicationFailed, (error) =>
        onErrorCallback(error, 'Video Patient/Guest - Error on track publication'),
      );
      return () => {
        room.localParticipant.off(LocalParticipantEvents.TrackPublicationFailed, (error) =>
          onErrorCallback(error, 'Video Patient/Guest - Error on track publication'),
        );
      };
    }
  }, [room, onErrorCallback]);

  const contextValue = useMemo(
    () => ({
      connect,
      getAudioAndVideoTracks,
      getLocalAudioTrack,
      getLocalVideoTrack,
      isAcquiringLocalTracks,
      isConnecting,
      localTracks,
      onError: onErrorCallback,
      removeLocalVideoTrack,
      room,
    }),
    [
      connect,
      getAudioAndVideoTracks,
      getLocalAudioTrack,
      getLocalVideoTrack,
      isAcquiringLocalTracks,
      isConnecting,
      localTracks,
      onErrorCallback,
      removeLocalVideoTrack,
      room,
    ],
  );

  return (
    <VideoContext.Provider value={contextValue}>
      <SelectedParticipantProvider room={room}>{children}</SelectedParticipantProvider>
    </VideoContext.Provider>
  );
};

export default VideoProvider;
